import React, { useState } from 'react';
import { AuthService } from '@/services/auth.service';
import { resendSignUpCode } from 'aws-amplify/auth';
import type { AuthView } from '../components/UserLogin/types';

export const useUserLogin = (onLoginSuccess: () => void) => {
  // Core Form Fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // UI State Controllers
  const [view, setView] = useState<AuthView>('login');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isChallengeReset, setIsChallengeReset] = useState(false);

  // Clear states when shifting between forms
  const handleViewChange = (newView: AuthView) => {
    setError(null);
    setSuccessMessage(null);
    setCode('');
    setNewPassword('');
    if (newView !== 'submit_new') setIsChallengeReset(false);
    setView(newView);
  };

  /**
   * Standard Username / Password Login Form Submit
   */
  const handleLoginSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields.');
      return;
    }
    try {
      setError(null);
      setSuccessMessage(null);
      setLoading(true);

      const { isSignedIn, nextStep } = await AuthService.signInWithCredentials(email, password);

      if (isSignedIn) {
        onLoginSuccess();
        return;
      }

      if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setIsChallengeReset(true);
        setSuccessMessage('Temporary password accepted. Please configure your permanent account password.');
        setView('submit_new');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Forgot Password - Request Verification Code Form Submit
   */
  const handleForgotPasswordSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      setLoading(true);

      await AuthService.requestPasswordReset(email);
      setIsChallengeReset(false);
      setSuccessMessage('A verification code has been dispatched to your email.');
      setView('submit_new');
    } catch (err: any) {
      const errMsg = err.message || '';

      if (errMsg.includes('no registered/verified email') || err.name === 'InvalidParameterException') {
        try {
          await resendSignUpCode({ username: email });
          setSuccessMessage(
            'Your account is not fully verified yet. We have sent an account activation code to your inbox instead.'
          );
        } catch (resendErr: any) {
          setError(resendErr.message || 'This account cannot be recovered. Please contact an administrator.');
        }
      } else {
        setError(errMsg || 'Error processing request. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Submit Verification Code & New Password Form Submit
   */
  const handleNewPasswordSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    if ((isChallengeReset && !newPassword) || (!isChallengeReset && (!code || !newPassword))) {
      setError('Please fill in all required fields.');
      return;
    }

    try {
      setError(null);
      setLoading(true);

      if (isChallengeReset) {
        const { isSignedIn } = await AuthService.confirmNewPasswordChallenge(newPassword);
        if (isSignedIn) {
          onLoginSuccess();
          return;
        }
      } else {
        await AuthService.submitNewPassword(email, code, newPassword);
      }

      setSuccessMessage('Password updated successfully! Please sign in with your new credentials.');
      handleViewChange('login');
    } catch (err: any) {
      setError(err.message || 'Invalid validation parameters or password strength requirements not met.');
    } finally {
      setLoading(false);
    }
  };

  return {
    // Field state
    email,
    setEmail,
    password,
    setPassword,
    code,
    setCode,
    newPassword,
    setNewPassword,
    // UI state
    view,
    error,
    successMessage,
    loading,
    isChallengeReset,
    // Handlers
    handleViewChange,
    handleLoginSubmit,
    handleForgotPasswordSubmit,
    handleNewPasswordSubmit,
  };
};
